import type { TokenResponse } from './types'

/**
 * Where the session lives between page loads.
 *
 * localStorage, matching the `cacheLocation: 'localstorage'` the Auth0 SDK was
 * configured with. It survives a reload and is shared across tabs of the same
 * origin, which is what the refresh lock below depends on.
 *
 * The trade-off is the one that came with the Auth0 configuration: script
 * running on this origin can read the tokens. Moving them out of reach needs the
 * IdP to set an HttpOnly cookie and the portal to stop holding a bearer token at
 * all, which is a change to the service's contract, not just to this file.
 */

const storageKey = 'passgenau.auth'

/** Refresh this many seconds before the access token actually expires. */
export const refreshSkewSeconds = 60

export interface StoredSession {
  accessToken: string
  refreshToken: string | undefined
  idToken: string | undefined
  /** Epoch milliseconds at which the access token expires. */
  expiresAt: number
}

/** Read the stored session, treating anything malformed as absent. */
export const readSession = (): StoredSession | undefined => {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(storageKey)
  } catch {
    // Storage is unavailable in a locked-down or private-mode browser. The
    // session then simply does not persist across loads.
    return undefined
  }
  if (!raw) return undefined

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return undefined

    const session = parsed as Record<string, unknown>
    if (typeof session.accessToken !== 'string' || typeof session.expiresAt !== 'number') {
      return undefined
    }

    return {
      accessToken: session.accessToken,
      refreshToken: typeof session.refreshToken === 'string' ? session.refreshToken : undefined,
      idToken: typeof session.idToken === 'string' ? session.idToken : undefined,
      expiresAt: session.expiresAt,
    }
  } catch {
    return undefined
  }
}

export const writeSession = (session: StoredSession): void => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(session))
  } catch {
    // Nothing to do: the session stays in memory for this page load only.
  }
}

export const clearSession = (): void => {
  try {
    window.localStorage.removeItem(storageKey)
  } catch {
    // Already unreachable; there is nothing left to clear.
  }
}

/** Turn a token response into what we persist, keeping the current refresh
 *  token when the response omits one. */
export const toStoredSession = (
  tokens: TokenResponse,
  previousRefreshToken?: string
): StoredSession => ({
  accessToken: tokens.access_token,
  refreshToken: tokens.refresh_token ?? previousRefreshToken,
  idToken: tokens.id_token ?? undefined,
  expiresAt: Date.now() + tokens.expires_in * 1000,
})

/** True once the access token is inside the refresh skew of expiring. */
export const isExpiring = (session: StoredSession): boolean =>
  session.expiresAt - refreshSkewSeconds * 1000 <= Date.now()

/**
 * Run `body` with an exclusive cross-tab lock.
 *
 * Refresh tokens rotate, and the IdP treats a second presentation of the same
 * token as theft: it revokes the whole chain and signs the person out
 * everywhere. Two tabs waking up together would do exactly that, so the refresh
 * has to be serialised across the whole origin rather than merely within this
 * page. The Web Locks API is the only thing that spans tabs; without it we fall
 * back to the in-page promise, which at least stops one tab racing itself.
 */
export const withRefreshLock = async <T>(body: () => Promise<T>): Promise<T> => {
  const locks = navigator.locks
  if (!locks) return body()

  // The DOM types declare the callback as synchronous, so `request` is inferred
  // here as Promise<Promise<T>>. Awaiting flattens it, as it does at runtime.
  return await locks.request(`${storageKey}.refresh`, body)
}

/** Notify subscribers when another tab signs in, signs out, or refreshes. */
export const onSessionChanged = (handler: (session: StoredSession | undefined) => void) => {
  const listener = (event: StorageEvent) => {
    if (event.key !== null && event.key !== storageKey) return
    handler(readSession())
  }

  window.addEventListener('storage', listener)
  return () => window.removeEventListener('storage', listener)
}
